// Lightweight toasts. useToast() works without a provider too (falls back to a
// no-op), so components stay testable and never crash.
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

const ICONS = { success: CheckCircle, error: AlertCircle, info: Info };
const STYLES = {
  success: 'border-green-200 bg-white text-green-800',
  error: 'border-red-200 bg-white text-red-800',
  info: 'border-blue-200 bg-white text-blue-800',
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const seq = useRef(0);

  const dismiss = useCallback((id) => setToasts((list) => list.filter((t) => t.id !== id)), []);

  const show = useCallback(
    (message, tone = 'success', { duration = 5000 } = {}) => {
      const id = ++seq.current;
      setToasts((list) => [...list, { id, message, tone }]);
      if (duration) setTimeout(() => dismiss(id), duration);
      return id;
    },
    [dismiss]
  );

  const api = useMemo(
    () => ({
      show,
      success: (m, o) => show(m, 'success', o),
      error: (m, o) => show(m, 'error', { duration: 8000, ...o }),
      info: (m, o) => show(m, 'info', o),
      dismiss,
    }),
    [show, dismiss]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2 px-4 sm:items-end sm:px-6" aria-live="polite">
        {toasts.map((t) => {
          const Icon = ICONS[t.tone] || Info;
          return (
            <div
              key={t.id}
              role={t.tone === 'error' ? 'alert' : 'status'}
              className={`pointer-events-auto flex w-full max-w-sm items-start gap-2 rounded-lg border p-3 text-sm shadow-lg ${STYLES[t.tone] || STYLES.info}`}
            >
              <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <p className="flex-1 text-gray-800">{t.message}</p>
              <button type="button" onClick={() => dismiss(t.id)} aria-label="Zavřít oznámení" className="rounded p-0.5 text-gray-500 hover:bg-gray-100">
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

const NOOP = { show: () => null, success: () => null, error: () => null, info: () => null, dismiss: () => {} };

export function useToast() {
  return useContext(ToastContext) || NOOP;
}
